import { ParsedUrlQuery } from "querystring";

import { ComponentType } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { Emptyable, Nullable, UserFormPermissions, UserGeneralPermissions } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

import { isProduction } from "../../../constants/env";
import { CurrentUserProvider } from "../../../contexts/CurrentUserContext";
import { EmailEditorProvider } from "../../../contexts/EmailEditorContext";
import { LocaleProvider } from "../../../contexts/LocaleContext";
import { StripeAccountProvider } from "../../../contexts/StripeAccountContext";
import { RedirectToLoginOnForbiddenGhostUserPage } from "../../../features/GhostSignup/RedirectToLoginOnForbiddenGhostUserPage";
import { RedirectOnUnsupportedDashboardBrowser } from "../../../features/UnsupportedBrowser/RedirectOnUnsupportedDashboardBrowser";
import { isAdminRoute, isDonorRoute, isOrganizationRoute } from "../../../helpers/auth";
import { useLogParamsEvent } from "../../../hooks/amplitude/useLogParamsEvent";
import { useDisableSafariBackForwardCache } from "../../../hooks/useDisableSafariBackForwardCache";
import { LayoutProps } from "../../../layouts/type";
import { AmplitudeExperienceProvider } from "../../../providers/AmplitudeExperienceProvider";
import { AmplitudeProvider } from "../../../providers/AmplitudeProvider";
import { OrganizationEngagementFlagsProvider } from "../../../providers/OrganizationEngagementFlagsProvider";
import { PageLoadingProvider } from "../../../providers/PageLoadingProvider";
import { DevContextCopy } from "../../DevContextCopy/DevContextCopy";
import { HubspotChatbot } from "../../HubspotChatbot/HubspotChatbot";
import { OrganizationCategoryPopup } from "../../OrganizationCategoryPopup/OrganizationCategoryPopup";
import { DonorRoute } from "../../Routes/DonorRoute";
import { OrganizationRoute } from "../../Routes/OrganizationRoute";
import { PublicRoute } from "../../Routes/PublicRoute";
import { UserConsent } from "../../UserConsent/UserConsent";

import { ClarityProviderWrapper } from "./ClarityProviderWrapper";

import { ZeffySnackbarProvider } from "@/dashboard-design-system/Snackbar";

import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/plugins.pkgd.min.css";

import "react-phone-input-2/lib/material.css";
import "react-phone-input-2/lib/style.css";

export const AppDashboardProvider = ({
  children,
  allowGhostUsers,
  layout,
  formPermissions,
  generalPermissions,
  noScreenPadding,
  disableFullHeightOnMobile,
}: {
  children: React.ReactNode;
  layout: ComponentType<LayoutProps>;
  formPermissions?: Nullable<UserFormPermissions[]>;
  generalPermissions?: Nullable<UserGeneralPermissions[]>;
  noScreenPadding?: boolean;
  disableFullHeightOnMobile?: boolean;
  allowGhostUsers?: boolean;
}) => {
  useLogParamsEvent();
  const router = useRouter();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  useDisableSafariBackForwardCache();

  return (
    <>
      {/* for router ready, see https://github.com/vercel/next.js/discussions/11484#discussioncomment-356055 */}
      <PageLoadingProvider isLoading={!router.isReady}>
        <CurrentUserProvider>
          <CssBaseline />
          <ClarityProviderWrapper>
            <AmplitudeProvider>
              <RedirectToLoginOnForbiddenGhostUserPage allowGhostUsers={allowGhostUsers || false}>
                <OrganizationEngagementFlagsProvider>
                  <AmplitudeExperienceProvider>
                    <StripeAccountProvider>
                      <LocaleProvider>
                        <ZeffySnackbarProvider>
                          <RedirectOnUnsupportedDashboardBrowser>
                            <CssBaseline />
                            <DevContextCopy formId={null} />
                            <OrganizationCategoryPopup />
                            <EmailEditorProvider>
                              <PermissionRoute
                                layout={layout}
                                formPermissions={formPermissions}
                                generalPermissions={generalPermissions}
                                noScreenPadding={noScreenPadding}
                                disableFullHeightOnMobile={disableFullHeightOnMobile}
                              >
                                {children}
                              </PermissionRoute>
                              {!isProduction && !hideHubspotChatbot(query, router.route) && <HubspotChatbot />}
                              <UserConsent />
                            </EmailEditorProvider>
                          </RedirectOnUnsupportedDashboardBrowser>
                        </ZeffySnackbarProvider>
                      </LocaleProvider>
                    </StripeAccountProvider>
                  </AmplitudeExperienceProvider>
                </OrganizationEngagementFlagsProvider>
              </RedirectToLoginOnForbiddenGhostUserPage>
            </AmplitudeProvider>
          </ClarityProviderWrapper>
        </CurrentUserProvider>
      </PageLoadingProvider>
    </>
  );
};

const HubspotChatflowTargets = {
  show: new Set(["o", "login", "register", "d"]),
  hide: new Set(["donation-form", "ticketing", "team", "peer-to-peer", "fundraising", "embed"]),
};

const hideHubspotChatbot = (query: ParsedUrlQuery, route: string) => {
  // Chatbot is hidden on form creation and edition
  if (Object.keys(query).length > 0 && (route.includes("/forms/edit") || route.includes("/forms/new"))) {
    return true;
  }

  // get first level path
  const path = route.split("/")[1];
  if (HubspotChatflowTargets.hide.has(path)) {
    return true;
  }

  return query.hideHubspotChatbot === "true" || !HubspotChatflowTargets.show.has(path);
};

const PermissionRoute = ({
  children,
  layout,
  formPermissions,
  generalPermissions,
  noScreenPadding,
  disableFullHeightOnMobile,
}: {
  children: React.ReactNode;
  layout: ComponentType<LayoutProps>;
  formPermissions?: Emptyable<UserFormPermissions[]>;
  generalPermissions?: Emptyable<UserGeneralPermissions[]>;
  noScreenPadding?: boolean;
  disableFullHeightOnMobile?: boolean;
}) => {
  const router = useRouter();
  useDisableSafariBackForwardCache();

  const isDonorPage = isDonorRoute(router.pathname);
  const isOrganizationPage = isOrganizationRoute(router.pathname);
  const isAdminPage = isAdminRoute(router.pathname);
  const isPublicPage = !isDonorPage && !isOrganizationPage && !isAdminPage;

  if (isDonorPage) {
    return <DonorRoute layout={layout}>{children}</DonorRoute>;
  }
  if (isOrganizationPage || isAdminPage) {
    return (
      <OrganizationRoute
        layout={layout}
        formPermissions={formPermissions}
        generalPermissions={generalPermissions}
        noScreenPadding={noScreenPadding}
        disableFullHeightOnMobile={disableFullHeightOnMobile}
      >
        {children}
      </OrganizationRoute>
    );
  }
  if (isPublicPage) {
    return (
      <PublicRoute layout={layout} noScreenPadding={noScreenPadding}>
        {children}
      </PublicRoute>
    );
  }

  return null;
};
